import { FC, useState } from 'react'
import {
  Text,
  TextInputField,
  SimpleModal,
  Button,
  Flex
} from '@sequencehq/core-components'
import { useForm, ValidationError } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import { useNotifications } from '@sequencehq/core-components'
import { useMutation, useQuery } from '@sequencehq/api'
import {
  dashboard20240730Client,
  DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import {
  Primary14SemiBold,
  GreyGrey80,
  Primary13SemiBold
} from '@sequencehq/core-components'
import { EventPropertyField } from './EventPropertyField'
import { UsageEventTypeSelect } from 'UsageEvents/index'

type FormValues = {
  customerAlias: string
  eventType: string
}

type EventPropertyField = {
  id: string
  eventProperty: string
  value: string
}

type Props = {
  isOpen: boolean
  onClose: () => void
  usageEvent: DashboardApi20240730.UsageEvents.UsageEvent
}

export const DuplicateUsageEventDrawer: FC<Props> = ({
  isOpen,
  onClose,
  usageEvent
}) => {
  const { displayNotification } = useNotifications()

  // Get usage event property options for dropdown
  const { data: usageEventPropertyData } = useQuery(
    dashboard20240730Client.getUniqueUsageEventProperties
  )

  const usageEventPropertyOptions =
    usageEventPropertyData?.items.map(v => ({
      id: v,
      value: v,
      label: v
    })) ?? []

  // Initialize event property fields from existing event
  const [eventPropertyFields, setEventPropertyFields] = useState<
    EventPropertyField[]
  >(() => {
    const existingProperties = Object.entries(
      usageEvent.eventProperties || {}
    ).map(([name, value]) => ({
      id: crypto.randomUUID(),
      eventProperty: name,
      value: String(value)
    }))

    if (existingProperties.length > 0) {
      return existingProperties
    }

    return [
      {
        id: crypto.randomUUID(),
        eventProperty: '',
        value: ''
      }
    ]
  })

  const form = useForm<FormValues>({
    value: {
      customerAlias: usageEvent.customerAlias,
      eventType: usageEvent.eventType
    },
    fieldConfiguration: [
      { property: 'customerAlias', validation: [required] },
      { property: 'eventType', validation: [required] }
    ]
  })

  const { mutateAsync: duplicateUsageEvent } = useMutation(
    dashboard20240730Client.postUsageEvent,
    {
      onSuccess: () => {
        displayNotification('Usage event updated successfully', {
          type: 'success'
        })
        onClose()
      },
      onError: () => {
        displayNotification('Failed to update usage event', {
          type: 'error'
        })
      }
    }
  )

  const handleSubmit = async () => {
    if (!form.queries.isValid) {
      // Display validation errors to user
      const fieldErrors = form.queries.validationResults
        .filter(error => error.message)
        .map(error => `${error.fieldName}: ${error.message}`)
        .join('\n')

      if (fieldErrors) {
        displayNotification(
          'Please fix the following errors:\n' + fieldErrors,
          {
            type: 'error',
            duration: 5000
          }
        )
      } else {
        displayNotification('Please complete all required fields', {
          type: 'error',
          duration: 5000
        })
      }
      return false
    }

    const eventProperties = eventPropertyFields.reduce(
      (acc, { eventProperty, value }) => {
        const trimmedProperty = eventProperty.trim()
        const trimmedValue = value.trim()

        if (!trimmedProperty || !trimmedValue) {
          return acc
        }
        return { ...acc, [trimmedProperty]: trimmedValue }
      },
      {} as Record<string, unknown>
    )

    const response = await duplicateUsageEvent({
      customerAlias: form.queries.formData.customerAlias,
      customerEventId: usageEvent.customerEventId,
      eventType: form.queries.formData.eventType,
      eventTimestamp: usageEvent.eventTimestamp,
      eventProperties
    })

    return !!response
  }

  const handleClose = () => {
    form.mutators.resetFormData()

    // Reset event property fields to original event data
    const originalProperties = Object.entries(
      usageEvent.eventProperties || {}
    ).map(([name, value]) => ({
      id: crypto.randomUUID(),
      eventProperty: name,
      value: String(value)
    }))

    if (originalProperties.length > 0) {
      setEventPropertyFields(originalProperties)
    } else {
      setEventPropertyFields([
        {
          id: crypto.randomUUID(),
          eventProperty: '',
          value: ''
        }
      ])
    }

    onClose()
  }

  const addEventProperty = () => {
    setEventPropertyFields([
      ...eventPropertyFields,
      {
        id: crypto.randomUUID(),
        eventProperty: '',
        value: ''
      }
    ])
  }

  const removeEventProperty = (id: string) => {
    setEventPropertyFields(eventPropertyFields.filter(field => field.id !== id))
  }

  const updateEventProperty = (
    id: string,
    field: 'eventProperty' | 'value',
    newValue: string
  ) => {
    setEventPropertyFields(prev =>
      prev.map(property =>
        property.id === id ? { ...property, [field]: newValue } : property
      )
    )
  }

  return (
    <SimpleModal
      data-testid="duplicateUsageEvent.drawer"
      isOpen={isOpen}
      variant="drawer"
      title="Overwrite usage event"
      cancelButtonText="Cancel"
      submitButtonText="Overwrite event"
      submittingButtonText="Updating..."
      onSubmit={handleSubmit}
      onClose={handleClose}
    >
      <Flex
        direction="column"
        gap="16px"
        data-testid="duplicateUsageEvent.form"
      >
        <Text fontSize="sm" color="gray.600" mb={2}>
          Creates a new event with the same ID, overwriting the previous one.
        </Text>

        <TextInputField
          label="Customer alias"
          value={form.fields.customerAlias.value}
          onChange={e => form.fields.customerAlias.onChange(e.target.value)}
          validationErrors={form.fields.customerAlias.validationErrors}
        />

        <TextInputField
          label="Customer event ID"
          value={usageEvent.customerEventId}
          disabled
        />

        <UsageEventTypeSelect
          value={form.fields.eventType.value}
          onChange={form.fields.eventType.onChange}
        />

        <TextInputField
          label="Event timestamp"
          value={usageEvent.eventTimestamp}
          disabled
        />

        <Flex direction="column" gap="8px" marginTop="8px">
          <Text {...Primary14SemiBold}>Event Properties</Text>
          <Flex alignContent="center" alignItems="center" gap={2}>
            <Text
              width="50%"
              {...Primary13SemiBold}
              color={GreyGrey80}
              display="flex"
              gap={1}
            >
              Event property{' '}
            </Text>
            <Text {...Primary13SemiBold} color={GreyGrey80}>
              Value
            </Text>
          </Flex>
          {eventPropertyFields.map(field => {
            // Basic validation: both property name and value should be provided together
            const hasPropertyName = field.eventProperty.trim().length > 0
            const hasValue = field.value.trim().length > 0
            const isIncomplete =
              (hasPropertyName && !hasValue) || (!hasPropertyName && hasValue)
            const validationErrors =
              isIncomplete && hasPropertyName && !hasValue
                ? [
                    {
                      message:
                        'Value is required when property name is provided'
                    } as ValidationError
                  ]
                : []

            return (
              <EventPropertyField
                key={field.id}
                options={usageEventPropertyOptions}
                eventProperty={{
                  value: field.eventProperty,
                  onChange: (value: string) => {
                    updateEventProperty(field.id, 'eventProperty', value)
                  }
                }}
                value={{
                  value: field.value,
                  onChange: (value: string) => {
                    updateEventProperty(field.id, 'value', value)
                  }
                }}
                canDelete={eventPropertyFields.length > 1}
                onDelete={() => removeEventProperty(field.id)}
                validationErrors={validationErrors}
              />
            )
          })}
          <Button variant="ghost" onClick={addEventProperty}>
            Add event property
          </Button>
        </Flex>
      </Flex>
    </SimpleModal>
  )
}
